import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { BenefitIcon } from "@/components/BenefitIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";

export default function Benefits() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/benefits/categories"), api.get("/products")])
      .then(([c, p]) => { setCategories(c.data); setProducts(p.data); })
      .finally(() => setLoading(false));
  }, []);

  const countFor = (key) => products.filter((p) => p.benefit_category_key === key).length;

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-7xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Benefits overview"
        title="Understand what's available"
        subtitle="Browse benefit categories in plain language. Tap any category to see the specific options offered to you."
        action={<DemoBadge className="mt-1" />}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-52 w-full rounded-xl" />)
          : categories.map((c, i) => (
              <Link
                key={c.id}
                to={`/app/benefits/${c.key}`}
                data-testid={`benefit-category-${c.key}`}
                className="group bg-white rounded-xl border border-mist-200 p-8 card-lift flex flex-col animate-rise"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-navy-800 flex items-center justify-center mb-5 group-hover:bg-gold transition-colors">
                  <BenefitIcon name={c.icon} className="w-7 h-7 text-gold group-hover:text-navy-900 transition-colors" />
                </div>
                <h3 className="font-display text-2xl font-bold text-navy-900">{c.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{c.plain_language}</p>
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-mist-200">
                  <span className="text-xs font-semibold text-navy-700">
                    {countFor(c.key)} option{countFor(c.key) === 1 ? "" : "s"} available
                  </span>
                  <span className="flex items-center gap-1 text-sm font-bold text-gold-dark">
                    Explore <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
      </div>
    </div>
  );
}
